import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getGamesDaily,
  getPopularDecks,
  getRecentGames,
  getStatsInbox,
  getStatsOverview,
} from "../../api/stats.service";

/**
 * Home dashboard stats: overview counters, daily games chart, popular decks.
 * Each bucket: { value, isLoading, error }.
 */

const isOk = (resp) => resp && resp.status === 200 && resp.data !== undefined;

const errMessage = (resp) => resp?.response?.data?.message || resp?.message || "Error fetching stats";

export const fetchOverview = createAsyncThunk("stats/fetchOverview", async (_, { rejectWithValue }) => {
  const resp = await getStatsOverview();
  if (!isOk(resp)) return rejectWithValue(errMessage(resp));
  return resp.data;
});

export const fetchGamesDaily = createAsyncThunk(
  "stats/fetchGamesDaily",
  async (days = 7, { rejectWithValue }) => {
    const resp = await getGamesDaily(days);
    if (!isOk(resp)) return rejectWithValue(errMessage(resp));
    return resp.data;
  }
);

export const fetchPopularDecks = createAsyncThunk(
  "stats/fetchPopularDecks",
  async (take = 5, { rejectWithValue }) => {
    const resp = await getPopularDecks(take);
    if (!isOk(resp)) return rejectWithValue(errMessage(resp));
    return resp.data;
  }
);

export const fetchRecentGames = createAsyncThunk(
  "stats/fetchRecentGames",
  async (take = 6, { rejectWithValue }) => {
    const resp = await getRecentGames(take);
    if (!isOk(resp)) return rejectWithValue(errMessage(resp));
    return resp.data;
  }
);

export const fetchInbox = createAsyncThunk("stats/fetchInbox", async (_, { rejectWithValue }) => {
  const resp = await getStatsInbox();
  if (!isOk(resp)) return rejectWithValue(errMessage(resp));
  return resp.data;
});

const emptyBucket = () => ({ value: null, isLoading: false, error: null });

const bucketReducers = (thunk, key) => ({
  [thunk.pending]: (state) => {
    state[key].isLoading = true;
    state[key].error = null;
  },
  [thunk.fulfilled]: (state, action) => {
    state[key].value = action.payload;
    state[key].isLoading = false;
    state[key].error = null;
  },
  [thunk.rejected]: (state, action) => {
    state[key].isLoading = false;
    state[key].error = action.payload || "error";
  },
});

export const statsSlice = createSlice({
  name: "stats",
  initialState: {
    overview: emptyBucket(),
    gamesDaily: emptyBucket(),
    popularDecks: emptyBucket(),
    recentGames: emptyBucket(),
    inbox: emptyBucket(),
  },
  reducers: {},
  extraReducers: {
    ...bucketReducers(fetchOverview, "overview"),
    ...bucketReducers(fetchGamesDaily, "gamesDaily"),
    ...bucketReducers(fetchPopularDecks, "popularDecks"),
    ...bucketReducers(fetchRecentGames, "recentGames"),
    ...bucketReducers(fetchInbox, "inbox"),
  },
});

export default statsSlice.reducer;
